














































































































































































































import { Component, Vue, Ref } from "vue-property-decorator";
import { ClientPanel, ClientPanelExtended, getClientPanelName } from "@/data/models/ClientPanel";
import { getAvailablePanels } from "@/data/repository/ClientPanelRepository";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";

import { SearchQueryDebouncer } from "@/utils/SearchQueryDebouncer";
import { ProjectDetails } from "@/data/models/ProjectDetails";
import {
  findBranch,
  findBranchByNumber,
  getBranchesOfClient,
} from "@/data/repository/ClientRepository";
import { BranchFilter } from "@/data/models/BranchFilter";
import { DeviceOverview } from "@/data/models/DeviceOverview";
import { fetchDeviceOverviews } from "@/data/repository/AllDevicesRepository";
import {
  isClientPanel,
  getCategoryIdFromSerialNumber,
  getTemplateIdFromSerialNumber,
  getUpdatedCategoryAndTemplateIds,
  CATEGORY_HY_DRY_ID,
  CATEGORY_COM_BOX_ID,
} from "@/data/models/DeviceSettingsManager";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import {
  defineCategoriesAndTemplatesClientAccess,
  manageAllDevicesClientAccess,
} from "@/permissions/ClientPermissions";
import { COLOR_PRIMARY } from "@/utils/colors";
import InfiniteLoading, { StateChanger } from "vue-infinite-loading";
import { findHiddenDeviceHint } from "@/utils/HiddenDeviceHint";

type FlatDevice = (DeviceOverview | ClientPanelExtended) & {
  __templateId: string;
  __categoryId: string;
};
 
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "infinite-loading": InfiniteLoading,
  },
})
export default class SelectDeviceDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  searchQueryDebouncer = new SearchQueryDebouncer(this.onSearchQueryChanged);
  searchQuery = "";

  pageSize = 20;
  searchInfiniteId = new Date();
  searchSliceLength = this.pageSize;

  isLoadingDevices = false;
  showNoDevicesAvailableHint = false;

  clientPanels: Array<ClientPanelExtended> = [];
  deviceOverviews: Array<DeviceOverview> = [];
  /** The client's devices left out of the list (in a project, or of another Standort), for the hint. */
  hiddenDevices: Array<ClientPanel | DeviceOverview> = [];
  matchesProjectBranch: (branchNumber: string | null | undefined) => boolean = () => true;

  selectedDeviceId = "";
  branchNameLookup = new Map<string, string>();
  expandedCategories: string[] = [];
  expandedTemplates: string[] = [];
  priorityIds = [CATEGORY_COM_BOX_ID, CATEGORY_HY_DRY_ID];

  /**
   * Iterates over each device overview, updating the category and template IDs based on shared device settings.
   * This method leverages the `getUpdatedCategoryAndTemplateIds` function to find the updated category and template IDs
   * from the shared device settings that correspond to the local settings for each device. It then updates these IDs
   * in the device overview list.
   *
   * @returns {DeviceOverview[]} An array of device overview objects with updated category and template IDs.
   */
  get deviceOverviewsWithUpdatedCategoryAndTemplateIds() {
    const deviceOverviews = this.deviceOverviews;

    deviceOverviews.forEach((device) => {
      const { updatedCategoryId, updatedTemplateId } = getUpdatedCategoryAndTemplateIds(
        device.categoryId,
        device.templateId,
        this.localDeviceSettingsJson,
        this.sharedDeviceSettingsJson,
        device.devicePowerUsageType ?? undefined,
      );

      device.categoryId = updatedCategoryId;
      device.templateId = updatedTemplateId;
    });

    return deviceOverviews;
  }

  get devicesByCategoryAndTemplate() {
    const devices = [...this.clientPanels, ...this.deviceOverviewsWithUpdatedCategoryAndTemplateIds];
    const result: Record<string, Record<string, (DeviceOverview | ClientPanelExtended)[]>> = {};

    for (const device of devices) {
      const categoryId = isClientPanel(device)
        ? getCategoryIdFromSerialNumber(device.serialNumber)
        : device.categoryId;
      const templateId = isClientPanel(device)
        ? getTemplateIdFromSerialNumber(device.serialNumber)
        : device.templateId;

      if (!result[categoryId]) {
        result[categoryId] = {};
      }
      if (!result[categoryId][templateId]) {
        result[categoryId][templateId] = [];
      }

      result[categoryId][templateId].push(device);
    }

    const priorityIds = this.priorityIds;
    const sortedKeys = Object.keys(result).sort((a, b) => {
      const aPriority = priorityIds.indexOf(a);
      const bPriority = priorityIds.indexOf(b);
      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;
      return a.localeCompare(b);
    });

    const sorted: typeof result = {};
    for (const key of sortedKeys) {
      sorted[key] = result[key];
    }
    return sorted;
  }

  get shouldRemovePadding() {
    return this.searchQuery ? true : false;
  }

  get colorPrimary() {
    return COLOR_PRIMARY;
  }

  get isAllowedToDefineOwnDeviceCategoriesAndTemplates() {
    return defineCategoriesAndTemplatesClientAccess();
  }

  get hasAllDevicesFeatureEnabled() {
    return manageAllDevicesClientAccess();
  }

  get sharedDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;
  }

  get localDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates;
  }

  get deviceSettingsJson() {
    return this.isAllowedToDefineOwnDeviceCategoriesAndTemplates
      ? this.localDeviceSettingsJson
      : this.sharedDeviceSettingsJson;
  }

  get filteredDevicesByCategoryAndTemplate() {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return this.devicesByCategoryAndTemplate;

    const filtered: Record<string, Record<string, (DeviceOverview | ClientPanelExtended)[]>> = {};

    for (const [categoryId, templates] of Object.entries(this.devicesByCategoryAndTemplate)) {
      const filteredTemplates: typeof templates = {};

      for (const [templateId, devices] of Object.entries(templates)) {
        const matches = devices.filter(device => {
          const label = this.getDeviceName(device).toLowerCase();
          return label.includes(query)
            || device.serialNumber?.toLowerCase().includes(query)
            || device.qrCode?.toLowerCase().includes(query);
        });

        if (matches.length > 0) {
          filteredTemplates[templateId] = matches;
        }
      }

      if (Object.keys(filteredTemplates).length > 0) {
        filtered[categoryId] = filteredTemplates;
      }
    }

    this.showNoDevicesAvailableHint = Object.keys(filtered).length === 0;
    return filtered;
  }

  /** Why an exactly typed device is not in the list, or "" when it is offered or unknown (#2610143). */
  get hiddenDeviceHintText(): string {
    const hint = findHiddenDeviceHint(
      this.searchQuery,
      this.hiddenDevices,
      this.matchesProjectBranch,
      (branchNumber) => findBranchByNumber(branchNumber)?.name
    );

    if (!hint) {
      return "";
    }

    return hint.kind === "assignedToProject"
      ? this.$t("assignDevice.deviceAssignedToOtherProject", [hint.projectName]).toString()
      : this.$t("assignDevice.deviceInOtherBranch", [hint.branchName]).toString();
  }

  get searchedDevicesToDisplay() {
    return this.flatFilteredDevices.slice(0, this.searchSliceLength);
  }

  get flatFilteredDevices(): FlatDevice[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return [];

    const devices: FlatDevice[] = [];

    for (const [categoryId, templates] of Object.entries(this.filteredDevicesByCategoryAndTemplate)) {
      for (const [templateId, deviceList] of Object.entries(templates)) {
        for (const device of deviceList) {
          devices.push({
            ...device,
            __templateId: templateId,
            __categoryId: categoryId,
          });
        }
      }
    }

    return devices;
  }

  get groupedFlatDevices(): Record<string, FlatDevice[]> {
    const result: Record<string, FlatDevice[]> = {};
    const seenCategories = new Set<string>();

    this.isLoadingDevices = true;
    for (const device of this.searchedDevicesToDisplay) {
      const categoryId = device.__categoryId;
      const templateId = device.__templateId;
      const key = `${categoryId}::${templateId}`;

      const attachCategoryId = !seenCategories.has(categoryId);
      if (attachCategoryId) {
        seenCategories.add(categoryId);
      }

      const newDevice = {
        ...device,
        __categoryId: attachCategoryId ? categoryId : "",
      };

      if (!result[key]) {
        result[key] = [];
      }

      result[key].push(newDevice);
    }
    this.isLoadingDevices = false;
    this.showNoDevicesAvailableHint = Object.keys(result).length === 0;

    return result;
  }

  onSearchQueryChanged(searchQuery: string) {
    this.showNoDevicesAvailableHint = false;
    this.searchQuery = searchQuery;
    this.searchSliceLength = this.pageSize;
    this.searchInfiniteId = new Date();
  }

  getBranchName(device: ClientPanel | DeviceOverview) {
    const branch = device.branch;
    if (!branch) {
      return null;
    }
    return this.branchNameLookup.get(branch);
  }

  getDeviceName(device: ClientPanel | DeviceOverview) {
    if (!isClientPanel(device) && device.serialNumber && device.serialNumber !== device.inventoryNumber) {
      return `${device.inventoryNumber} / ${device.serialNumber}`;
    }
    return getClientPanelName(device);
  }

  toggleCategory(categoryId: string) {
    const index = this.expandedCategories.indexOf(categoryId);
    if (index >= 0) {
      this.expandedCategories.splice(index, 1);
    } else {
      this.expandedCategories.push(categoryId);
    }
  }

  toggleTemplate(templateId: string) {
    const index = this.expandedTemplates.indexOf(templateId);
    if (index >= 0) {
      this.expandedTemplates.splice(index, 1);
    } else {
      this.expandedTemplates.push(templateId);
    }
  }

  resolve?: (value: ClientPanel | undefined) => void = undefined;
  reject?: (reason: string) => void = undefined;

  isClientPanel(device: ClientPanelExtended | DeviceOverview) {
    return isClientPanel(device) ? true : false;
  }

  getCategoryName(categoryId: string): string {
    const match = this.deviceSettingsJson?.deviceCategories?.find(cat => cat.id === categoryId);
    return match?.categoryName ?? "";
  }

  getTemplateName(templateId: string): string {
    const allTemplates = this.deviceSettingsJson?.deviceCategories?.flatMap(cat => cat.deviceTemplates) || [];
    const match = allTemplates.find(t => t.id === templateId);
    return match?.deviceName ?? "";
  }

  // TODO: Edit so if vehicle management feature is enabled, load devices from vehicles.
  async show(projectDetails: ProjectDetails) {
    this.selectedDeviceId = "";
    this.searchQuery = "";
    this.hiddenDevices = [];

    const targetBranchId = projectDetails.clientData.branch;
    let targetBranchFilter: BranchFilter | null = null;

    if (targetBranchId) {
      const branch = findBranch(targetBranchId) ?? null;
      if (branch) {
        targetBranchFilter = new BranchFilter(false, branch);
      }
    }

    getBranchesOfClient().forEach((branch) => {
      this.branchNameLookup.set(branch.branchNumber.toString(), branch.name);
    });

    this.baseDialogComponent.show({
      title: this.$t("assignDevice.assignDevice").toString(),
      positiveButtonText: this.$t("common.assign").toString(),
      scrollable: true,
      maxWidth: 800,
    });

    this.matchesProjectBranch = (branchNumber) =>
      !targetBranchFilter || targetBranchFilter.doesMatchBranchNumber(branchNumber);

    this.isLoadingDevices = true;
    const panels = (await getAvailablePanels()).filter((c) => c.availability !== -1);
    const isOfferedPanel = (c: ClientPanel) =>
      (!c.currentProjectId || c.currentProjectId === "") && this.matchesProjectBranch(c.branch);

    this.clientPanels = panels
      .filter(isOfferedPanel)
      .sort((c1, c2) => c1.serialNumber.localeCompare(c2.serialNumber))
      .map((c) => ({ ...c, id: c.serialNumber } as ClientPanelExtended));

    const overviews = this.hasAllDevicesFeatureEnabled
      ? (await fetchDeviceOverviews()).filter((d) => d.availability !== -1)
      : [];
    const isOfferedOverview = (d: DeviceOverview) =>
      !d.currentlyAssigned && this.matchesProjectBranch(d.branch);

    this.deviceOverviews = overviews
      .filter(isOfferedOverview)
      .sort((d1, d2) => d1.inventoryNumber.localeCompare(d2.inventoryNumber));

    this.hiddenDevices = [
      ...panels.filter((c) => !isOfferedPanel(c)),
      ...overviews.filter((d) => !isOfferedOverview(d)),
    ];

    this.isLoadingDevices = false;

    this.showNoDevicesAvailableHint = this.clientPanels.length === 0 && this.deviceOverviews.length === 0;

    return new Promise<ClientPanel | DeviceOverview | undefined>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  async searchInfiniteHandler(data: StateChanger) {
    const nextSliceLength = this.searchSliceLength + this.pageSize;
    this.searchSliceLength = nextSliceLength;

    if (nextSliceLength >= this.flatFilteredDevices.length) {
      data.complete();
    } else {
      data.loaded();
    }
  }

  onSave() {
    const selectedClientPanel = this.clientPanels.find(
      (clientPanel) => clientPanel.serialNumber === this.selectedDeviceId
    ) as ClientPanel;
    const selectedDeviceOverview = this.deviceOverviews.find(
      (deviceOverview) => deviceOverview.id === this.selectedDeviceId
    );

    const result = selectedClientPanel ?? selectedDeviceOverview;

    this.resolve && this.resolve(result);
  }

  onCancel() {
    this.resolve && this.resolve(undefined);
  }
}
